import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TIERS, GRACE_DAY, billingIsLocked, formatPrice, billingMonthLabel } from '../../lib/billing';

// Next-month pre-pay opens 6 days before the 1st. e.g. May billing
// becomes available on Apr 25. Expressed as "today + 6 days is in a
// different month than today" so it works naturally across all month
// lengths and the year boundary.
const NEXT_MONTH_OPEN_DAYS = 6;

function computeNextMonthOpen(now) {
  const probe = new Date(now);
  probe.setDate(now.getDate() + NEXT_MONTH_OPEN_DAYS);
  return (
    probe.getMonth() !== now.getMonth() ||
    probe.getFullYear() !== now.getFullYear()
  );
}

export function Billing() {
  const { profile, refreshBilling } = useAuth();
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const dayOfMonth = now.getDate();
  // Next calendar month (handles the Dec → Jan year rollover).
  const nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const nextMonth = nextDate.getMonth() + 1;
  const nextYear = nextDate.getFullYear();
  const nextMonthOpen = computeNextMonthOpen(now);

  const [billingRecord, setBillingRecord] = useState(undefined);
  // The next-month billing row, when `nextMonthOpen` is true. Null
  // when the coach hasn't assigned the plan yet; undefined while
  // loading.
  const [nextRecord, setNextRecord] = useState(undefined);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  // Tracks which row Stripe is redirecting for (current or next).
  const [payingId, setPayingId] = useState(null);

  const showSuccess = new URLSearchParams(window.location.search).get('success') === '1';

  useEffect(() => {
    if (profile) load();
  }, [profile]);

  // After Stripe redirect, poll for webhook to update paid_at
  useEffect(() => {
    if (!showSuccess || !profile) return;
    let stopped = false;
    const poll = setInterval(async () => {
      if (stopped) return;
      const { data } = await supabase
        .from('billing')
        .select('paid_at')
        .eq('athlete_id', profile.id)
        .eq('month', currentMonth)
        .eq('year', currentYear)
        .maybeSingle();
      if (data?.paid_at) {
        stopped = true;
        clearInterval(poll);
        load();
        refreshBilling();
      }
    }, 2000);
    const timeout = setTimeout(() => { stopped = true; clearInterval(poll); }, 30000);
    return () => { stopped = true; clearInterval(poll); clearTimeout(timeout); };
  }, [showSuccess, profile]);

  async function load() {
    setLoading(true);
    // Fetch current month + next month (when the pre-pay window is
    // open) + recent history in parallel. Rows are created by the
    // existing billing flow — this page just reads and displays.
    const queries = [
      supabase
        .from('billing')
        .select('*')
        .eq('athlete_id', profile.id)
        .eq('month', currentMonth)
        .eq('year', currentYear)
        .maybeSingle(),
      supabase
        .from('billing')
        .select('*')
        .eq('athlete_id', profile.id)
        .order('year', { ascending: false })
        .order('month', { ascending: false })
        .limit(12),
    ];
    if (nextMonthOpen) {
      queries.push(
        supabase
          .from('billing')
          .select('*')
          .eq('athlete_id', profile.id)
          .eq('month', nextMonth)
          .eq('year', nextYear)
          .maybeSingle()
      );
    }
    const results = await Promise.all(queries);
    const current = results[0].data;
    const hist = results[1].data;
    const next = nextMonthOpen ? results[2].data : null;
    setBillingRecord(current ?? null);
    setNextRecord(nextMonthOpen ? (next ?? null) : undefined);
    setHistory(
      (hist ?? []).filter(
        r =>
          !(r.month === currentMonth && r.year === currentYear) &&
          !(r.month === nextMonth && r.year === nextYear)
      )
    );
    setLoading(false);
  }

  async function handlePayStripe(record = billingRecord) {
    if (!record) return;
    setPaying(true);
    setPayingId(record.id);
    try {
      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billingIds: [record.id] }),
      });
      const { url, error } = await res.json();
      if (error) throw new Error(error);
      window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
      setPaying(false);
      setPayingId(null);
    }
  }

  const isPaid = !!billingRecord?.paid_at;
  const isLocked = billingIsLocked(billingRecord);
  const isPending = billingRecord && !billingRecord.paid_at;
  const tier = billingRecord?.tier ? TIERS[billingRecord.tier] : null;

  if (loading) {
    return (
      <Layout>
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ marginBottom: 24 }}>
        <p className="page-eyebrow">Account</p>
        <h1 className="page-title">Billing</h1>
      </div>

      {showSuccess && (
        <div className="billing-success-banner">
          Payment received — your training access has been restored. Thank you!
        </div>
      )}

      {/* ── 1. Payment Card (merged status + action) ───────────────── */}
      <div
        className="billing-tier-card"
        style={{
          cursor: 'default',
          marginBottom: 24,
          borderColor: isPaid ? '#16a34a' : isLocked ? '#dc2626' : isPending ? '#f59e0b' : 'var(--gray-200)',
          background: isPaid ? '#f0fdf4' : isLocked ? '#fef2f2' : isPending ? '#fffbeb' : 'var(--white)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>{billingMonthLabel(currentMonth, currentYear)}</div>
            {billingRecord && (
              <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>
                {tier?.label ?? billingRecord.tier} · {formatPrice(billingRecord.amount_cents)}/month
              </div>
            )}
          </div>
          {isPaid && <span className="billing-badge billing-badge--paid">Paid</span>}
          {isPending && dayOfMonth <= GRACE_DAY && <span className="billing-badge billing-badge--pending">Due by the {GRACE_DAY}th</span>}
          {isLocked && <span className="billing-badge billing-badge--overdue">Overdue</span>}
          {!billingRecord && <span className="billing-badge billing-badge--pending">No plan assigned</span>}
        </div>

        {isPaid && (
          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
            Confirmed {new Date(billingRecord.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        )}

        {isPending && (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <button className="btn-stripe-pay" onClick={() => handlePayStripe(billingRecord)} disabled={paying}>
              {paying && payingId === billingRecord.id ? 'Redirecting to Stripe…' : `Pay ${formatPrice(billingRecord.amount_cents)} with Card`}
            </button>
            <p style={{ fontSize: 11, color: 'var(--gray-400)', margin: 0 }}>Secured by Stripe. Your card is never stored.</p>
          </div>
        )}

        {!billingRecord && (
          <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 8, marginBottom: 0 }}>
            Your coach hasn't assigned a plan for this month yet. Check back soon.
          </p>
        )}
      </div>

      {/* ── 1b. Next-month pre-pay card ─────────────────────────────
          Opens on day 25 of the current month (or equivalent for
          shorter months). Silently hides before that window — no
          need to tease something the athlete can't act on. */}
      {nextMonthOpen && nextRecord !== undefined && (() => {
        const nextTier = nextRecord?.tier ? TIERS[nextRecord.tier] : null;
        const nextPaid = !!nextRecord?.paid_at;
        const nextPending = nextRecord && !nextRecord.paid_at;
        return (
          <div
            className="billing-tier-card"
            style={{
              cursor: 'default',
              marginBottom: 24,
              borderColor: nextPaid ? '#16a34a' : nextPending ? '#3b82f6' : 'var(--gray-200)',
              background: nextPaid ? '#f0fdf4' : nextPending ? '#eff6ff' : 'var(--white)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>
                  {billingMonthLabel(nextMonth, nextYear)}
                </div>
                {nextRecord ? (
                  <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>
                    {nextTier?.label ?? nextRecord.tier} · {formatPrice(nextRecord.amount_cents)}/month · <span style={{ color: '#3b82f6', fontWeight: 600 }}>Pay early</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>
                    Upcoming billing period
                  </div>
                )}
              </div>
              {nextPaid && <span className="billing-badge billing-badge--paid">Paid</span>}
              {nextPending && <span className="billing-badge billing-badge--pending">Available now</span>}
            </div>

            {nextPaid && (
              <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
                Confirmed {new Date(nextRecord.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            )}

            {nextPending && (
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <button className="btn-stripe-pay" onClick={() => handlePayStripe(nextRecord)} disabled={paying}>
                  {paying && payingId === nextRecord.id ? 'Redirecting to Stripe…' : `Pay ${formatPrice(nextRecord.amount_cents)} with Card`}
                </button>
                <p style={{ fontSize: 11, color: 'var(--gray-400)', margin: 0 }}>Secured by Stripe. Your card is never stored.</p>
              </div>
            )}

            {!nextRecord && (
              <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 8, marginBottom: 0 }}>
                Your coach hasn't set up next month's plan yet — it'll appear here as soon as they do.
              </p>
            )}
          </div>
        );
      })()}

      {/* ── 2. Plan & Referral side by side ─────────────────────────── */}
      {tier && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div className={`billing-tier-card${isPaid ? ' billing-tier-card--paid' : ''}`} style={{ cursor: 'default' }}>
            <div className="billing-tier-badge" style={isPaid ? { background: '#16a34a' } : {}}>
              {isPaid ? 'Active' : 'Current Plan'}
            </div>
            <div className="billing-tier-name">{tier.label}</div>
            <ul className="billing-tier-features">
              {tier.features.map(f => <li key={f}>{f}</li>)}
            </ul>
            <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 16, marginBottom: 0 }}>
              Plan changes are managed by your coach. Contact them if you'd like to adjust.
            </p>
          </div>
          <div className="billing-tier-card" style={{ cursor: 'default', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10, justifyContent: 'center' }}>
            <div style={{ fontSize: 32, lineHeight: 1 }}>🎁</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>Refer an Athlete</div>
            <p style={{ fontSize: 13, color: 'var(--gray-600)', margin: 0, maxWidth: 260 }}>
              Know someone who'd benefit from coaching? If they sign up, you get <strong>1 month free</strong>.
            </p>
            <p style={{ fontSize: 11, color: 'var(--gray-400)', margin: 0 }}>
              Just let your coach know who you referred.
            </p>
          </div>
        </div>
      )}

      {/* ── Payment History ─────────────────────────────────────────── */}
      {history.length > 0 && (
        <div className="section-card" style={{ marginTop: 8 }}>
          <div className="section-card-header">
            <span className="section-card-title">Payment History</span>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map(row => (
                <tr key={row.id}>
                  <td>{billingMonthLabel(row.month, row.year)}</td>
                  <td>{TIERS[row.tier]?.label ?? row.tier}</td>
                  <td>
                    {formatPrice(row.amount_cents)}
                    {row.amount_cents === 0 && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: '#16a34a', fontWeight: 600 }}>Credit Applied</span>
                    )}
                  </td>
                  <td>
                    {row.paid_at
                      ? <span className="billing-badge billing-badge--paid">Paid</span>
                      : <span className="billing-badge billing-badge--overdue">Unpaid</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
